import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Modal, ModalProps, Stack} from "@mantine/core";
import {useCallback} from "react";
import {FreightTypeEntity, getObjectId, SiteEntity} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";

interface DispatchRequestFilterFormProps {
  defaultValues?: Partial<DispatchRequestFilterFormValues>;
  onSubmit?: (values: DispatchRequestFilterFormValues) => void;
}

export interface DispatchRequestFilterFormValues {
  pickupSite?: SiteEntity | string;
  deliverySite?: SiteEntity | string;
  freightType?: FreightTypeEntity | string;
}

export const DEFAULT_DISPATCH_REQUEST_FILTER: DispatchRequestFilterFormValues = {
  pickupSite: undefined,
  deliverySite: undefined,
  freightType: undefined,
}

export const isDefaultDispatchRequestFilter = (filter1: DispatchRequestFilterFormValues): boolean => {
  if (filter1 === DEFAULT_DISPATCH_REQUEST_FILTER) {
    return true
  }

  if (getObjectId(filter1.pickupSite) !== getObjectId(DEFAULT_DISPATCH_REQUEST_FILTER.pickupSite)) {
    return false;
  }

  if (getObjectId(filter1.deliverySite) !== getObjectId(DEFAULT_DISPATCH_REQUEST_FILTER.deliverySite)) {
    return false;
  }

  if (getObjectId(filter1.freightType) !== getObjectId(DEFAULT_DISPATCH_REQUEST_FILTER.freightType)) {
    return false;
  }

  return true;
}

export const DispatchRequestRequestFilterForm = (props: DispatchRequestFilterFormProps) => {
  const {
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: {
      pickupSite: getObjectId(defaultValues?.pickupSite),
      deliverySite: getObjectId(defaultValues?.deliverySite),
      freightType: getObjectId(defaultValues?.freightType),
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        pickupSite: values.pickupSite,
        deliverySite: values.deliverySite,
        freightType: values.freightType,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <SiteSelect
          label={t("common.entity.dispatchrequest.filter.form.field.pickupsite", "Pickup Site")}
          placeholder={t<string>("common.entity.dispatchrequest.filter.form.field.pickupsite", "Pickup Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("pickupSite")} />
        <SiteSelect
          label={t("common.entity.dispatchrequest.filter.form.field.deliverysite", "Delivery Site")}
          placeholder={t<string>("common.entity.dispatchrequest.filter.form.field.deliverysite", "Delivery Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("deliverySite")} />
        <FreightTypeSelect
          label={t("common.entity.dispatchrequest.filter.form.field.freighttype", "Freight Type")}
          placeholder={t<string>("common.entity.dispatchrequest.filter.form.field.freighttype", "Freight Type")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("freightType")} />
      </Stack>
      <Button type="submit" fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface DispatchRequestFilterFormModalProps extends DispatchRequestFilterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const DispatchRequestFilterFormModal = (props: DispatchRequestFilterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <DispatchRequestRequestFilterForm defaultValues={defaultValues} onSubmit={onSubmit}/>
    </Modal>
  );
};
